import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button, CircularProgress, Divider, Drawer, Typography } from '@mui/material';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import StickyHeader from 'app/shared/componentsRestyled/common/StickyHeader';
import PopTipCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopTipCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconOrientation, IconSize } from 'constants/coreDesignSystem';
import { LoanChangesHistoryState } from 'constants/loanApplication';
import { CURRENT_USER_AUTHOR_LABEL } from 'constants/pendingChangesAuthors';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { annotatePendingChanges, buildAuthorColorMap } from 'utils/pendingChangesAuthors';

import { setActiveTabWithNavigationAction } from 'store/loanApplications/actions';

import { ApplicationChangesHistoryActionType } from '../../LoanInfoHeader/LoanChangesHistoryTable/LoanChangesHistoryTable.constants';
import { normalizeLoanChangesHistoryTableData } from '../../LoanInfoHeader/LoanChangesHistoryTable/LoanChangesHistoryTable.utils';
import useCustomFieldHistoryContext from '../hooks/useCustomFieldHistoryContext';
import useDrawerAuthorKeys from '../hooks/useDrawerAuthorKeys';
import useFeeHistoryContext from '../hooks/useFeeHistoryContext';
import usePendingChangesAuthorsControls from '../hooks/usePendingChangesAuthorsControls';
import { tooltipWrapperStyles } from '../LoanInfoHeader.styles';

import PendingChangesFilterBar from './components/PendingChangesFilterBar';
import PendingChangesOperationGroup from './components/PendingChangesOperationGroup';
import PendingChangesSectionGroup from './components/PendingChangesSectionGroup';
import PendingChangesSidebar from './components/PendingChangesSidebar';
import {
  ALL_SECTIONS_KEY,
  ChangeKindFilter,
  paperProps,
  PENDING_CHANGES_SUBTAB,
  PendingChangesDrawerActionLabel,
  slideProps,
} from './PendingChangesDrawer.constants';
import {
  bodyStyles,
  cancelButtonStyles,
  changesSummaryStyles,
  closeButtonStyles,
  contentWrapperStyles,
  drawerStyles,
  emptyStateStyles,
  expandCollapseButtonStyles,
  headerActionsStyles,
  headerStyles,
  headerTitleStyles,
  loaderWrapperStyles,
  mainContentStyles,
  progressStyles,
  saveButtonStyles,
} from './PendingChangesDrawer.styles';
import { PendingChangesDrawerTestIds } from './PendingChangesDrawer.testIds';
import {
  buildDrawerAuthorsFromPendingChangesAuthors,
  buildOperationGroups,
  buildSectionTreesByKind,
  checkHasCauseInformation,
  filterChangesByAuthor,
  filterChangesByAuthorKeys,
  filterChangesBySearch,
  filterChangesByTreeSelection,
  filterOperationGroups,
  filterOperationGroupsBySearch,
  filterOperationGroupsBySection,
  getChangeKindCounts,
  getChangeType,
  getDirectChanges,
  getMenuChangesByKind,
  getSectionKeptInTree,
  getRecalculatedOnlyChanges,
  getSectionSummary,
  getUniqueAuthors,
  groupChangesBySection,
  labelAiPendingChanges,
  pollForElement,
  scrollToField,
} from './PendingChangesDrawer.utils';

const PendingChangesDrawer = ({
  onSaveChanges,
  onCancelChanges,
  isSaveDisabled = false,
  isCancelDisabled = false,
  isCancelHidden = false,
  saveDisabledTooltip = '',
  saveButtonLabel = PendingChangesDrawerActionLabel.SAVE,
  cancelButtonLabel = PendingChangesDrawerActionLabel.CANCEL,
}) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const { applicationId, tab, subtab, entityId, section, subsection } = useParams();

  const isOpen = subtab === PENDING_CHANGES_SUBTAB;

  const [rawLoanChangesHistory, setRawLoanChangesHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(ALL_SECTIONS_KEY);
  const [changeKindFilter, setChangeKindFilter] = useState(ChangeKindFilter.DIRECT);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroupKeys, setExpandedGroupKeys] = useState({});

  const loanId = useLoanId();

  const customFieldContext = useCustomFieldHistoryContext();
  const feeTypeContext = useFeeHistoryContext();

  const {
    isEnabled: isPendingChangesByUserEnabled,
    authors: pendingChangesAuthors,
    selectedAuthorKeys: pendingChangesSelectedAuthorKeys,
  } = usePendingChangesAuthorsControls();

  // The chips open on the button's selection and only filter this list: the loan behind the drawer keeps
  // showing the button's selection.
  const {
    selectedAuthorKeys: drawerAuthorKeys,
    onAuthorToggle: onDrawerAuthorToggle,
    onResetAuthorKeys,
  } = useDrawerAuthorKeys(pendingChangesSelectedAuthorKeys);

  const onClose = useCallback(() => {
    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        entityId,
        section,
        subsection,
      }),
    );
  }, [history, applicationId, tab, entityId, section, subsection]);

  const changes = useMemo(() => {
    const normalizedChanges = normalizeLoanChangesHistoryTableData(
      isPendingChangesByUserEnabled
        ? annotatePendingChanges(rawLoanChangesHistory)
        : rawLoanChangesHistory,
      customFieldContext,
      feeTypeContext,
    );

    const typedChanges = normalizedChanges.map(change => ({
      ...change,
      changeType: getChangeType(change.actionType),
    }));

    return isPendingChangesByUserEnabled
      ? labelAiPendingChanges(typedChanges, pendingChangesAuthors)
      : typedChanges;
  }, [
    rawLoanChangesHistory,
    customFieldContext,
    feeTypeContext,
    isPendingChangesByUserEnabled,
    pendingChangesAuthors,
  ]);

  const authors = useMemo(
    () =>
      isPendingChangesByUserEnabled
        ? buildDrawerAuthorsFromPendingChangesAuthors(
            pendingChangesAuthors,
            CURRENT_USER_AUTHOR_LABEL,
          )
        : getUniqueAuthors(changes),
    [isPendingChangesByUserEnabled, pendingChangesAuthors, changes],
  );

  const authorColorMap = useMemo(
    () =>
      isPendingChangesByUserEnabled
        ? buildAuthorColorMap(pendingChangesAuthors, ({ key }) => key)
        : buildAuthorColorMap(authors),
    [isPendingChangesByUserEnabled, pendingChangesAuthors, authors],
  );

  const selectedAuthorKeys = useMemo(
    () => (isPendingChangesByUserEnabled ? drawerAuthorKeys : _.compact([selectedAuthor])),
    [isPendingChangesByUserEnabled, drawerAuthorKeys, selectedAuthor],
  );

  const onAuthorToggle = useCallback(
    authorKey => {
      if (isPendingChangesByUserEnabled) {
        onDrawerAuthorToggle(authorKey);

        return;
      }

      setSelectedAuthor(prevAuthor => (prevAuthor === authorKey ? null : authorKey));
    },
    [isPendingChangesByUserEnabled, onDrawerAuthorToggle],
  );

  const filterByAuthor = useCallback(
    changesToFilter =>
      isPendingChangesByUserEnabled
        ? filterChangesByAuthorKeys(changesToFilter, selectedAuthorKeys)
        : filterChangesByAuthor(changesToFilter, selectedAuthor),
    [isPendingChangesByUserEnabled, selectedAuthorKeys, selectedAuthor],
  );

  const filteredByAuthor = useMemo(() => filterByAuthor(changes), [filterByAuthor, changes]);

  const filteredBySearchTerm = useMemo(
    () => filterChangesBySearch(filteredByAuthor, searchQuery),
    [filteredByAuthor, searchQuery],
  );

  const filteredChanges = useMemo(
    () => filterChangesByTreeSelection(filteredBySearchTerm, selectedSection, ALL_SECTIONS_KEY),
    [filteredBySearchTerm, selectedSection],
  );

  const hasCauseInformation = useMemo(() => checkHasCauseInformation(changes), [changes]);

  const operationGroups = useMemo(() => buildOperationGroups(changes), [changes]);

  const operationGroupsByAuthor = useMemo(
    () => filterOperationGroups(operationGroups, filterByAuthor),
    [operationGroups, filterByAuthor],
  );

  const operationGroupsBySearch = useMemo(
    () => filterOperationGroupsBySearch(operationGroupsByAuthor, searchQuery),
    [operationGroupsByAuthor, searchQuery],
  );

  const displayedOperationGroups = useMemo(
    () =>
      filterOperationGroupsBySection(operationGroupsBySearch, selectedSection, changeKindFilter),
    [operationGroupsBySearch, selectedSection, changeKindFilter],
  );

  const menuChangesByKind = useMemo(
    () => getMenuChangesByKind(operationGroupsByAuthor, searchQuery),
    [operationGroupsByAuthor, searchQuery],
  );

  const changeKindCounts = useMemo(
    () => getChangeKindCounts(menuChangesByKind),
    [menuChangesByKind],
  );

  const sectionTreesByKind = useMemo(
    () => buildSectionTreesByKind(menuChangesByKind),
    [menuChangesByKind],
  );

  const isRecalculatedSelected = changeKindFilter === ChangeKindFilter.RECALCULATED;

  // Recalculated values without a change behind them are still pending: they keep the list from reading
  // as empty.
  const listedChanges = useMemo(
    () =>
      hasCauseInformation
        ? [
            ...getDirectChanges(operationGroupsBySearch),
            ...getRecalculatedOnlyChanges(operationGroupsBySearch),
          ]
        : filteredBySearchTerm,
    [hasCauseInformation, operationGroupsBySearch, filteredBySearchTerm],
  );

  const hasAffectedChanges = useMemo(
    () => displayedOperationGroups.some(({ affectedChanges }) => affectedChanges.length > 0),
    [displayedOperationGroups],
  );

  const displayGroupedChanges = useMemo(
    () => groupChangesBySection(filteredChanges),
    [filteredChanges],
  );

  // Under Recalculated every group's recalculated values open, as does a group a search narrowed; each
  // stays open until toggled.
  const checkIsAffectedExpanded = useCallback(
    group => expandedGroupKeys[group.key] ?? (isRecalculatedSelected || !!group.isAffectedNarrowed),
    [expandedGroupKeys, isRecalculatedSelected],
  );

  const onToggleAffected = useCallback(
    groupKey => {
      const group = displayedOperationGroups.find(({ key }) => key === groupKey);

      setExpandedGroupKeys(prev => ({ ...prev, [groupKey]: !checkIsAffectedExpanded(group) }));
    },
    [displayedOperationGroups, checkIsAffectedExpanded],
  );

  const onExpandAllClick = useCallback(() => {
    setExpandedGroupKeys(
      Object.fromEntries(displayedOperationGroups.map(({ key }) => [key, true])),
    );
  }, [displayedOperationGroups]);

  const onCollapseAllClick = useCallback(() => {
    setExpandedGroupKeys(
      Object.fromEntries(displayedOperationGroups.map(({ key }) => [key, false])),
    );
  }, [displayedOperationGroups]);

  const onSearch = useCallback(query => {
    setSearchQuery(query);
    setExpandedGroupKeys({});
  }, []);

  const onSectionSelect = useCallback(sectionKey => {
    setSelectedSection(sectionKey);
    setExpandedGroupKeys({});
  }, []);

  const onChangeKindSelect = useCallback(
    kind => {
      setChangeKindFilter(kind);
      setSelectedSection(prev => getSectionKeptInTree(sectionTreesByKind[kind], prev));
      setExpandedGroupKeys({});
    },
    [sectionTreesByKind],
  );

  const handleNavigate = useCallback(
    change => {
      onClose();

      if (!_.isNil(change.navigation)) {
        dispatch(
          setActiveTabWithNavigationAction({
            ...change.navigation,
            loanId,
            applicationId,
          }),
        );

        return;
      }

      if (_.isNil(change.tabName)) {
        return;
      }

      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: change.tabName,
          loanId,
          applicationId,
        }),
      );

      const fieldSelector = `[data-field-path="${change.propertyName}"], [data-field-name="${change.propertyName}"]`;

      pollForElement(fieldSelector, scrollToField);
    },
    [dispatch, onClose, loanId, applicationId],
  );

  const handleSaveChanges = useCallback(() => {
    onClose();
    onSaveChanges();
  }, [onClose, onSaveChanges]);

  const handleCancelChanges = useCallback(() => {
    onClose();
    onCancelChanges();
  }, [onClose, onCancelChanges]);

  useEffectSafeStrictMode(() => {
    if (!isOpen) {
      return;
    }

    setIsLoading(true);
    setSelectedSection(ALL_SECTIONS_KEY);
    setChangeKindFilter(ChangeKindFilter.DIRECT);
    setSelectedAuthor(null);
    onResetAuthorKeys();
    setSearchQuery('');
    setExpandedGroupKeys({});

    const action = ApplicationChangesHistoryActionType[LoanChangesHistoryState.PENDING];

    dispatch(action({ applicationId, historyState: LoanChangesHistoryState.PENDING }))
      .then(({ payload: changesHistory }) => {
        const loanChangesHistoryFlattened = Object.values(changesHistory || {}).flat();
        setRawLoanChangesHistory(loanChangesHistoryFlattened);
      })
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, isOpen, applicationId, onResetAuthorKeys]);

  return (
    <Drawer
      PaperProps={paperProps}
      SlideProps={slideProps}
      anchor="right"
      data-testid={PendingChangesDrawerTestIds.DRAWER}
      open={isOpen}
      sx={drawerStyles}
      onClose={onClose}
    >
      <Box sx={contentWrapperStyles}>
        <Box sx={headerStyles}>
          <Typography sx={headerTitleStyles} variant="bold16">
            Pending Changes
          </Typography>
          <Box sx={headerActionsStyles}>
            <PopTipCDS title={saveDisabledTooltip}>
              <Box component="span" sx={tooltipWrapperStyles}>
                <Button
                  color="primary"
                  data-testid={PendingChangesDrawerTestIds.SAVE_BUTTON}
                  disabled={isSaveDisabled}
                  sx={saveButtonStyles}
                  variant="contained"
                  onClick={handleSaveChanges}
                >
                  {saveButtonLabel}
                </Button>
              </Box>
            </PopTipCDS>
            {!isCancelHidden && (
              <Button
                data-testid={PendingChangesDrawerTestIds.CANCEL_BUTTON}
                disabled={isCancelDisabled}
                sx={cancelButtonStyles}
                variant="outlined"
                onClick={handleCancelChanges}
              >
                {cancelButtonLabel}
              </Button>
            )}
            <Button
              data-testid={PendingChangesDrawerTestIds.CLOSE_BUTTON}
              sx={closeButtonStyles}
              variant="text"
              onClick={onClose}
            >
              <CancelIcon size={IconSize[18]} />
            </Button>
          </Box>
        </Box>

        <Box sx={mainContentStyles}>
          <PendingChangesSidebar
            sections={sectionTreesByKind[changeKindFilter]}
            selectedKind={changeKindFilter}
            selectedSection={selectedSection}
            totalCounts={changeKindCounts}
            onKindSelect={onChangeKindSelect}
            onSectionSelect={onSectionSelect}
          />

          <Divider orientation={IconOrientation.VERTICAL} />

          <Box sx={bodyStyles}>
            {isLoading && (
              <Box sx={loaderWrapperStyles}>
                <CircularProgress size={50} sx={progressStyles} />
              </Box>
            )}

            {!isLoading && !changes.length && (
              <Box sx={emptyStateStyles}>
                <Typography variant="regular14">No pending changes found</Typography>
              </Box>
            )}

            {!isLoading && !!changes.length && (
              <>
                <StickyHeader>
                  <PendingChangesFilterBar
                    authorColorMap={authorColorMap}
                    authors={authors}
                    selectedAuthorKeys={selectedAuthorKeys}
                    onAuthorToggle={onAuthorToggle}
                    onSearch={onSearch}
                  />
                </StickyHeader>

                {hasCauseInformation && hasAffectedChanges && (
                  <Box sx={changesSummaryStyles}>
                    <Button
                      data-testid={PendingChangesDrawerTestIds.EXPAND_ALL_BUTTON}
                      sx={expandCollapseButtonStyles}
                      variant="outlined"
                      onClick={onExpandAllClick}
                    >
                      Expand all
                    </Button>
                    <Button
                      data-testid={PendingChangesDrawerTestIds.COLLAPSE_ALL_BUTTON}
                      sx={expandCollapseButtonStyles}
                      variant="outlined"
                      onClick={onCollapseAllClick}
                    >
                      Collapse all
                    </Button>
                  </Box>
                )}

                {!listedChanges.length && (
                  <Box sx={emptyStateStyles}>
                    <Typography variant="regular14">
                      No pending changes match the current filters
                    </Typography>
                  </Box>
                )}

                {hasCauseInformation &&
                  displayedOperationGroups.map(group => (
                    <PendingChangesOperationGroup
                      authorColorMap={authorColorMap}
                      group={group}
                      isAffectedExpanded={checkIsAffectedExpanded(group)}
                      key={group.key}
                      onNavigate={handleNavigate}
                      onToggleAffected={onToggleAffected}
                    />
                  ))}

                {!hasCauseInformation &&
                  Object.entries(displayGroupedChanges).map(([sectionName, sectionChanges]) => {
                    const summary = getSectionSummary(sectionChanges);

                    return (
                      <PendingChangesSectionGroup
                        authorColorMap={authorColorMap}
                        changeCount={summary.changeCount}
                        changes={sectionChanges}
                        key={sectionName}
                        sectionName={sectionName}
                        userCount={summary.userCount}
                        onNavigate={handleNavigate}
                      />
                    );
                  })}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

PendingChangesDrawer.propTypes = {
  onSaveChanges: PropTypes.func.isRequired,
  onCancelChanges: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool,
  isCancelDisabled: PropTypes.bool,
  isCancelHidden: PropTypes.bool,
  saveDisabledTooltip: PropTypes.node,
  saveButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
};

export default memo(PendingChangesDrawer);
